import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { hoursToMilliseconds } from 'date-fns';
import { useTrials } from 'hooks/use-trials/use-trials';
import { listOrganizations } from 'sync/organization-service/organization-service';
import { defaultPaginationQuery, defaultSortQuery } from 'sync/query';
import { PaginationParams, SortParams } from 'sync/request.types';
import { queryClient, ReactQueryKeys } from 'utils/react-query';
import { OrganizationRecord } from './use-organization-list.types';
import { getOrganizationRecords } from './use-organization-list.utils';

type UseOrganizationListProps = {
  fetchTrials?: boolean;
  refetchInterval?: number | false;
  paginationDetails?: PaginationParams;
  sortDetails?: SortParams;
};

/**
 * Specifies how long response is cached in localStorage.
 */
const ORGANIZATION_LIST_CACHE_TIME = hoursToMilliseconds(10);

export const useOrganizationList = ({
  fetchTrials = false,
  refetchInterval = false,
  paginationDetails = defaultPaginationQuery,
  sortDetails = defaultSortQuery,
}: UseOrganizationListProps = {}) => {
  const organizationListQuery = useQuery(
    ReactQueryKeys.ORGANIZATION_LIST(),
    () => listOrganizations({ ...paginationDetails, ...sortDetails }),
    {
      refetchInterval,
      cacheTime: ORGANIZATION_LIST_CACHE_TIME,
    }
  );

  const organizationList = useMemo(() => organizationListQuery.data?.data || [], [organizationListQuery.data?.data]);
  const organizationsById = useMemo(() => {
    return Object.fromEntries(organizationList.map((organization) => [organization.data.id, organization]));
  }, [organizationList]);

  const {
    trialsByOrganizationId,
    invalidateTrials,
    isLoading: areTrialsLoading,
  } = useTrials({
    trialContexts: fetchTrials
      ? organizationList.map((organization) => ({
          organizationId: organization.data.id,
          simulate: false,
        }))
      : [],
  });

  const organizationRecords = useMemo(
    () => getOrganizationRecords(trialsByOrganizationId, organizationsById),
    [organizationsById, trialsByOrganizationId]
  );

  const invalidate = () => {
    invalidateTrials();
    queryClient.invalidateQueries([ReactQueryKeys.ORGANIZATION_LIST()]);
  };

  const mustGetFirstOrganizationIdAvailable = () => {
    return Object.keys(organizationRecords)[0] || null;
  };

  const getById = (organizationId: string): OrganizationRecord | null => {
    return organizationRecords[organizationId] || null;
  };

  return {
    organizationListQuery,
    organizationList,
    organizationRecords,
    areTrialsLoading,
    areOrganizationRecordsLoading: organizationListQuery.isLoading || areTrialsLoading,
    isOrganizationListLoading: organizationListQuery.isLoading,
    isOrganizationFetched: organizationListQuery.isFetched,
    invalidate,
    getById,
    mustGetFirstOrganizationIdAvailable,
  };
};
